import styled from "styled-components";
import { memo, useCallback, useEffect, useState } from "react";
import {
  Plus,
  Trash2,
  Check,
  Lock,
  Loader2,
  AlertTriangle,
  Timer,
  Moon,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  BedDouble,
} from "lucide-react";
import { Head, router } from "@inertiajs/react";

import { theme } from "../_kit/theme";
import PageShell from "../_kit/PageShell";
import { useT } from "../_kit/i18n";
import { shiftIso, wibTodayIso } from "../_kit/dateFormat";
import DatePickField from "../_kit/DatePickField";
import ConfirmDialog from "../_kit/ConfirmDialog";
import PhoneField from "../_kit/PhoneField";
import { apiFetch } from "../_kit/api";

const rupiah = (value) => `Rp ${Number(value || 0).toLocaleString("id-ID")}`;

const Wrap = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  /* Trailing space so the fixed bottom-right dock (max ~50vh) can never cover the submit bar — the FO
     can always scroll the submit button clear of the dock. */
  padding: 1rem 0.5rem calc(50vh + 4rem);
`;

const Panel = styled.section`
  background: #ffffff;
  border: 1px solid ${theme.border};
  border-radius: 14px;
  padding: 1rem 1.15rem;
  margin-bottom: 1rem;
`;

const PanelTitle = styled.h2`
  margin: 0 0 0.8rem;
  font-size: 1.05rem;
  font-weight: 800;
  color: ${theme.ink};
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 0.8rem;
`;

// Live nights count next to the date fields — recomputes from checkIn/checkOut on every keystroke so
// the FO always sees the stay length. Amber when the range is invalid (0 nights).
const NightsBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  align-self: flex-end;
  padding: 0.45rem 0.8rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 800;
  background: ${(props) => (props.$invalid ? "#fffbeb" : theme.goldSoft)};
  color: ${(props) => (props.$invalid ? "#92400e" : theme.goldDark)};
  border: 1px solid ${(props) => (props.$invalid ? "#fde68a" : theme.gold)};
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  font-size: 0.72rem;
  font-weight: 700;
  color: ${theme.muted};
`;

const Select = styled.select`
  min-width: 220px;
  padding: 0.45rem 0.6rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.9rem;
  font-weight: 600;
  color: ${theme.ink};
`;

const NumberInput = styled.input`
  width: 90px;
  padding: 0.45rem 0.6rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 0.5rem 0.7rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  font-size: 0.95rem;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1rem;
  border: 0;
  border-radius: 8px;
  background: ${theme.gold};
  color: #ffffff;
  font-weight: 800;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: ${theme.goldDark};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const GhostButton = styled(Button)`
  background: #ffffff;
  color: ${theme.ink700};
  border: 1px solid ${theme.border};
  &:hover:not(:disabled) {
    background: ${theme.canvas};
  }
`;

const ItemCard = styled.div`
  border: 1px solid ${(props) => (props.$confirmed ? "#a7d7b0" : theme.border)};
  border-radius: 12px;
  padding: 0.9rem 1rem;
  margin-bottom: 0.8rem;
  background: ${(props) => (props.$confirmed ? "#f3fbf5" : "#ffffff")};
`;

const ItemHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
  margin-bottom: 0.6rem;
`;

const ItemHeadRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ConfirmRow = styled.div`
  margin-top: 0.6rem;
`;

const GuestField = styled.label`
  display: flex;
  flex: 1 1 200px;
  flex-direction: column;
  gap: 0.2rem;
  font-size: 0.72rem;
  font-weight: 700;
  color: ${theme.muted};
`;

const ItemName = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 1rem;
  font-weight: 800;
  color: ${theme.ink};
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.12rem 0.5rem;
  border-radius: 999px;
  background: ${(props) => (props.$confirmed ? "#dcfce7" : "#eef1f5")};
  color: ${(props) => (props.$confirmed ? "#166534" : theme.muted)};
  font-size: 0.72rem;
  font-weight: 800;
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.35rem 0.55rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  background: #ffffff;
  color: #b91c1c;
  font-weight: 700;
  font-size: 0.8rem;
  cursor: pointer;
`;

const RoomGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin: 0.5rem 0 0.7rem;
`;

const RoomCell = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 84px;
  padding: 0.4rem 0.55rem;
  border: 0;
  border-radius: 8px;
  background: ${(props) =>
    props.$selected ? "#c8e6c9" : props.$blocked ? "#fffbeb" : "#ffffff"};
  box-shadow: ${(props) =>
    props.$selected
      ? "inset 0 0 0 2px #4caf50"
      : props.$blocked
        ? "inset 0 0 0 1.5px #f59e0b"
        : `inset 0 0 0 1px ${theme.border}`};
  color: ${theme.ink};
  font-size: 0.9rem;
  font-weight: 800;
  cursor: pointer;
`;

// A blocked room is pickable but visibly flagged so the FO chooses it knowingly.
const BlockedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.05rem 0.32rem;
  border-radius: 5px;
  background: #f59e0b;
  color: #ffffff;
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.02em;
`;

const BlockedNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.35rem;
  margin-top: 0.45rem;
  padding: 0.45rem 0.6rem;
  border-radius: 8px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
  font-size: 0.82rem;
  line-height: 1.4;
`;

// A checkbox drawn right on the room-number cell, so it reads as clearly pickable (ticks when chosen).
const RoomTick = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  border-radius: 5px;
  border: 1.5px solid ${(props) => (props.$on ? "#2e7d32" : theme.border)};
  background: ${(props) => (props.$on ? "#4caf50" : "#ffffff")};
  color: #ffffff;
`;

const RateLine = styled.div`
  font-size: 0.9rem;
  color: ${theme.ink700};
`;

const ItemError = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin-top: 0.5rem;
  color: #b91c1c;
  font-size: 0.85rem;
  font-weight: 700;
`;

const Muted = styled.p`
  color: ${theme.muted};
  font-size: 0.9rem;
`;

const SummaryBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.8rem;
  padding: 0.9rem 1.15rem;
  border-radius: 12px;
  background: ${theme.canvas};
  border: 1px solid ${theme.border};
`;

const Total = styled.div`
  font-size: 1.05rem;
  font-weight: 800;
  color: ${theme.ink};
`;

const PageError = styled.div`
  margin-bottom: 0.8rem;
  padding: 0.6rem 0.8rem;
  border-radius: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  font-weight: 700;
`;

// Live countdown for the eZee holds. Neutral, then amber near expiry, then red once lapsed — so the FO
// finishes (or the page auto-releases) before the holds silently revert.
const HoldBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.8rem;
  padding: 0.55rem 0.8rem;
  border-radius: 8px;
  font-weight: 800;
  font-size: 0.9rem;
  background: ${(props) =>
    props.$level === "out"
      ? "#fef2f2"
      : props.$level === "warn"
        ? "#fffbeb"
        : theme.canvas};
  border: 1px solid
    ${(props) =>
      props.$level === "out"
        ? "#fecaca"
        : props.$level === "warn"
          ? "#fde68a"
          : theme.border};
  color: ${(props) =>
    props.$level === "out"
      ? "#b91c1c"
      : props.$level === "warn"
        ? "#92400e"
        : theme.ink700};
`;

// Memoized so a keystroke in the guest form (or the 1s hold-countdown tick) never re-renders the room
// grids. Re-renders only when its own item / busy / stable callbacks change.
const CartItemCard = memo((props) => {
  const { t } = useT();
  const item = props.item;
  const confirmed = item.status === "confirmed";
  return (
    <ItemCard $confirmed={confirmed}>
      <ItemHead>
        <ItemName>
          <BedDouble size={16} /> {item.label} · {item.quantity}{" "}
          {t("mdb_rooms_unit")}
        </ItemName>
        <ItemHeadRight>
          <Badge $confirmed={confirmed}>
            {confirmed ? (
              <>
                <Lock size={11} /> {t("mdb_status_confirmed")}
              </>
            ) : (
              t("mdb_status_draft")
            )}
          </Badge>
          <IconButton type="button" onClick={() => props.onRemove(item)}>
            <Trash2 size={13} /> {t("mdb_remove")}
          </IconButton>
        </ItemHeadRight>
      </ItemHead>

      {confirmed ? (
        <RateLine>
          {t("mdb_selected_rooms")}:{" "}
          <strong>
            {item.rooms
              .filter((room) => item.selectedRoomIds.includes(room.id))
              .map((room) => room.code)
              .join(", ")}
          </strong>
          {item.rateInfo ? (
            <> · {item.rateInfo.total_all_rooms_formatted}</>
          ) : null}
          {item.rooms.some(
            (room) => item.selectedRoomIds.includes(room.id) && room.blocked,
          ) ? (
            <>
              {" "}
              <BlockedBadge>{t("mdb_blocked_badge")}</BlockedBadge>
            </>
          ) : null}
        </RateLine>
      ) : (
        <>
          {item.rooms.length === 0 ? (
            <Muted>{t("mdb_no_rooms")}</Muted>
          ) : (
            <RoomGrid>
              {item.rooms.map((room) => {
                const selected = item.selectedRoomIds.includes(room.id);
                const blockedTitle = room.blocked_all
                  ? t("mdb_blocked_all")
                  : `${t("mdb_blocked_partial")}: ${(room.blocked_dates || []).join(", ")}`;
                return (
                  <RoomCell
                    key={room.id}
                    type="button"
                    $selected={selected}
                    $blocked={room.blocked}
                    aria-pressed={selected}
                    title={room.blocked ? blockedTitle : undefined}
                    onClick={() => props.onToggleRoom(item.sessionId, room.id)}
                  >
                    <RoomTick $on={selected}>
                      {selected ? <Check size={12} /> : null}
                    </RoomTick>
                    {room.code}
                    {room.blocked ? (
                      <BlockedBadge>{t("mdb_blocked_badge")}</BlockedBadge>
                    ) : null}
                  </RoomCell>
                );
              })}
            </RoomGrid>
          )}
          {item.selectedRoomIds.some(
            (id) => item.rooms.find((room) => room.id === id)?.blocked,
          ) ? (
            <BlockedNote>
              <AlertTriangle size={14} /> {t("mdb_blocked_pick_warning")}
            </BlockedNote>
          ) : null}
          {item.rateInfo ? (
            <RateLine>
              {item.rateInfo.number_of_nights} {t("nights")} ·{" "}
              {item.rateInfo.total_per_room_formatted} / {t("mdb_per_room")} ·{" "}
              <strong>{item.rateInfo.total_all_rooms_formatted}</strong>
            </RateLine>
          ) : null}
          <ConfirmRow>
            <Button
              type="button"
              onClick={() => props.onConfirm(item)}
              disabled={
                props.busy || item.selectedRoomIds.length !== item.quantity
              }
            >
              <Lock size={14} /> {t("mdb_confirm_rooms")} (
              {item.selectedRoomIds.length}/{item.quantity})
            </Button>
          </ConfirmRow>
          {item.error ? (
            <ItemError>
              <AlertTriangle size={14} /> {item.error}
            </ItemError>
          ) : null}
        </>
      )}
    </ItemCard>
  );
});
CartItemCard.displayName = "CartItemCard";

// Memoized guest form: typing here re-renders only this small form (its own local <input>s), never the
// cart/room grids. Lifts each field up through a stable onChange patch.
const GuestForm = memo((props) => {
  const { t } = useT();
  return (
    <Row>
      <GuestField>
        {t("full_name")} *
        <TextInput
          value={props.guest.name}
          onChange={(event) => props.onChange({ name: event.target.value })}
        />
      </GuestField>
      <GuestField>
        {t("phone")}
        <PhoneField
          value={props.guest.phone}
          onChange={(value) => props.onChange({ phone: value })}
        />
      </GuestField>
      <GuestField>
        {t("email")}
        <TextInput
          value={props.guest.email}
          inputMode="email"
          onChange={(event) => props.onChange({ email: event.target.value })}
        />
      </GuestField>
    </Row>
  );
});
GuestForm.displayName = "GuestForm";

const Dock = styled.aside`
  position: fixed;
  right: 1rem;
  bottom: 1rem;
  z-index: 900;
  width: 320px;
  max-width: calc(100vw - 2rem);
  max-height: 50vh;
  overflow-y: auto;
  background: #ffffff;
  border: 1px solid ${theme.border};
  border-radius: 12px;
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.16);
  padding: 0.6rem 0.75rem;
`;

const DockSection = styled.div`
  &:not(:last-child) {
    margin-bottom: 0.6rem;
    padding-bottom: 0.6rem;
    border-bottom: 1px solid ${theme.border};
  }
`;

const DockTitle = styled.div`
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: ${theme.muted};
  margin-bottom: 0.4rem;
`;

const DockCurrent = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.6rem;
  border-radius: 8px;
  background: ${theme.goldSoft};
  color: ${theme.goldDark};
  font-weight: 800;
  font-variant-numeric: tabular-nums;
`;

const DockRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.35rem 0;
`;

const DockMain = styled.div`
  font-size: 0.88rem;
  font-weight: 700;
  color: ${theme.ink};
`;

const DockSub = styled.div`
  font-size: 0.76rem;
  color: ${theme.muted};
  font-variant-numeric: tabular-nums;
`;

const MiniButton = styled.button`
  flex: 0 0 auto;
  padding: 0.28rem 0.6rem;
  border: 1px solid ${theme.gold};
  border-radius: 8px;
  background: ${theme.goldSoft};
  color: ${theme.goldDark};
  font-size: 0.78rem;
  font-weight: 800;
  cursor: pointer;
  &:hover {
    background: ${theme.gold};
    color: #ffffff;
  }
`;

const DockHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.4rem;
`;

const ToggleBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  padding: 0.15rem 0.4rem;
  border: 1px solid ${theme.border};
  border-radius: 999px;
  background: #ffffff;
  color: ${theme.ink700};
  font-size: 0.72rem;
  font-weight: 800;
  cursor: pointer;
  &:hover {
    border-color: ${theme.gold};
  }
`;

const RecentCard = styled.div`
  border: 1px solid ${theme.border};
  border-left: 3px solid ${theme.gold};
  border-radius: 8px;
  padding: 0.45rem 0.55rem;
  &:not(:last-child) {
    margin-bottom: 0.4rem;
  }
`;

const RecentTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

const RecentVoucher = styled.span`
  font-size: 0.86rem;
  font-weight: 800;
  color: ${theme.ink};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RoomsPill = styled.span`
  flex: 0 0 auto;
  padding: 0.05rem 0.4rem;
  border-radius: 999px;
  background: ${theme.canvas};
  border: 1px solid ${theme.border};
  font-size: 0.7rem;
  font-weight: 800;
  color: ${theme.ink700};
`;

const RecentGuest = styled.div`
  font-size: 0.78rem;
  color: ${theme.ink700};
  margin-top: 0.15rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RecentTime = styled.div`
  font-size: 0.72rem;
  color: ${theme.muted};
  font-variant-numeric: tabular-nums;
  margin-top: 0.1rem;
`;

const RecentOpen = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.35rem;
  padding: 0.2rem 0.55rem;
  border: 0;
  border-radius: 7px;
  background: ${theme.gold};
  color: #ffffff;
  font-size: 0.75rem;
  font-weight: 800;
  cursor: pointer;
  &:hover {
    background: ${theme.goldDark};
  }
`;

// Bottom-right dock: the live hold countdown (always visible while working), any OTHER in-progress cart
// that can be resumed after a mistaken refresh, and recently-created bookings to reopen.
const RecoveryDock = memo((props) => {
  const { t } = useT();
  const [recentOpen, setRecentOpen] = useState(false);
  const others = props.activeCarts.filter(
    (entry) => entry.cart_id !== props.currentCartId,
  );
  const countdown = (iso) => {
    if (!iso) {
      return "";
    }
    const total = Math.max(0, Math.floor((Date.parse(iso) - props.now) / 1000));
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
  };
  const hasCurrent = Boolean(props.currentCartId && props.cartExpiresAt);
  if (!hasCurrent && others.length === 0 && props.recentBookings.length === 0) {
    return null;
  }
  return (
    <Dock>
      {hasCurrent ? (
        <DockSection>
          <DockTitle>{t("mdb_dock_current")}</DockTitle>
          <DockCurrent>
            <Timer size={13} /> {countdown(props.cartExpiresAt)}
          </DockCurrent>
        </DockSection>
      ) : null}
      {others.length ? (
        <DockSection>
          <DockTitle>{t("mdb_dock_resume")}</DockTitle>
          {others.map((entry) => (
            <DockRow key={entry.cart_id}>
              <div>
                <DockMain>
                  {entry.check_in_date} → {entry.check_out_date}
                </DockMain>
                <DockSub>
                  {entry.items.length} {t("mdb_room_type")} ·{" "}
                  {countdown(entry.expires_at)}
                </DockSub>
              </div>
              <MiniButton type="button" onClick={() => props.onResume(entry)}>
                {t("mdb_resume")}
              </MiniButton>
            </DockRow>
          ))}
        </DockSection>
      ) : null}
      {props.recentBookings.length ? (
        <DockSection>
          <DockHead>
            <DockTitle>{t("mdb_dock_recent")}</DockTitle>
            {props.recentBookings.length > 1 ? (
              <ToggleBtn
                type="button"
                onClick={() => setRecentOpen((open) => !open)}
                aria-expanded={recentOpen}
              >
                {recentOpen ? (
                  <>
                    <ChevronUp size={13} /> {t("mdb_recent_less")}
                  </>
                ) : (
                  <>
                    <ChevronDown size={13} />{" "}
                    {t("mdb_recent_more").replace(
                      "%{n}",
                      Math.min(5, props.recentBookings.length),
                    )}
                  </>
                )}
              </ToggleBtn>
            ) : null}
          </DockHead>
          {props.recentBookings.slice(0, recentOpen ? 5 : 1).map((booking) => (
            <RecentCard key={booking.id}>
              <RecentTop>
                <RecentVoucher>
                  {booking.voucher_code || booking.guest_name || "—"}
                </RecentVoucher>
                <RoomsPill>
                  {booking.rooms_count} {t("mdb_rooms_unit")}
                </RoomsPill>
              </RecentTop>
              {booking.guest_name ? (
                <RecentGuest>{booking.guest_name}</RecentGuest>
              ) : null}
              {booking.created_at_label ? (
                <RecentTime>{booking.created_at_label}</RecentTime>
              ) : null}
              {booking.line_id ? (
                <RecentOpen
                  type="button"
                  onClick={() => props.onOpenBooking(booking.line_id)}
                >
                  <ExternalLink size={12} /> {t("mdb_open")}
                </RecentOpen>
              ) : null}
            </RecentCard>
          ))}
        </DockSection>
      ) : null}
    </Dock>
  );
});
RecoveryDock.displayName = "RecoveryDock";

const DirectBookingPage = (props) => {
  const { t } = useT();
  const initialDate = props.date || wibTodayIso();
  const [checkIn, setCheckIn] = useState(initialDate);
  const [checkOut, setCheckOut] = useState(() => shiftIso(initialDate, 1));
  const [cart, setCart] = useState(null);
  const [items, setItems] = useState([]);
  const [addType, setAddType] = useState("");
  const [addQty, setAddQty] = useState(1);
  const [guest, setGuest] = useState({ name: "", phone: "", email: "" });
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [cartExpiresAt, setCartExpiresAt] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const [activeCarts, setActiveCarts] = useState([]);
  const [recentBookings, setRecentBookings] = useState([]);

  // Tick every second whenever there's anything to count down (current or resumable carts). Carts are
  // NOT auto-cancelled on leave/refresh — the 15-min TTL + revert job is the abandoned-cart backstop, so
  // a mistaken refresh can be resumed from the dock instead of losing the held rooms.
  useEffect(() => {
    if (!cartExpiresAt && activeCarts.length === 0) {
      return undefined;
    }
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [cartExpiresAt, activeCarts.length]);

  const loadRecovery = useCallback(async () => {
    const [cartsRes, recentRes] = await Promise.all([
      apiFetch("/frontdesk_v2/direct_booking/active_carts").catch(() => null),
      apiFetch("/frontdesk_v2/direct_booking/recent").catch(() => null),
    ]);
    if (cartsRes?.data) {
      setActiveCarts(cartsRes.data.carts || []);
    }
    if (recentRes?.data) {
      setRecentBookings(recentRes.data.bookings || []);
    }
  }, []);

  useEffect(() => {
    loadRecovery();
  }, [loadRecovery]);

  const resumeCart = useCallback((entry) => {
    setCheckIn(entry.check_in_date);
    setCheckOut(entry.check_out_date);
    setCart({ cartId: entry.cart_id, roomTypes: entry.room_types || [] });
    setItems(
      (entry.items || []).map((item) => ({
        sessionId: item.session_id,
        roomTypeId: item.room_type_id,
        label: item.room_type_label,
        quantity: item.number_of_rooms,
        status: item.status === "hold_confirmed" ? "confirmed" : "draft",
        rooms: item.rooms || [],
        selectedRoomIds: item.selected_room_ids || [],
        rateInfo: item.rate_info,
        error: "",
      })),
    );
    setCartExpiresAt(entry.expires_at || null);
    setError("");
  }, []);

  const openBooking = useCallback(
    (lineId) => router.visit(`/frontdesk_v2/rooms_grid?drawer=${lineId}`),
    [],
  );

  const patchItem = useCallback(
    (sessionId, patch) =>
      setItems((prev) =>
        prev.map((item) =>
          item.sessionId === sessionId ? { ...item, ...patch } : item,
        ),
      ),
    [],
  );

  // Stable so the memoized CartItemCards never re-render on a guest keystroke / countdown tick.
  const onGuestChange = useCallback(
    (patch) => setGuest((prev) => ({ ...prev, ...patch })),
    [],
  );

  const onToggleRoom = useCallback(
    (sessionId, roomId) =>
      setItems((prev) =>
        prev.map((item) => {
          if (item.sessionId !== sessionId) {
            return item;
          }
          if (item.selectedRoomIds.includes(roomId)) {
            return {
              ...item,
              selectedRoomIds: item.selectedRoomIds.filter(
                (id) => id !== roomId,
              ),
            };
          }
          if (item.selectedRoomIds.length < item.quantity) {
            return {
              ...item,
              selectedRoomIds: [...item.selectedRoomIds, roomId],
            };
          }
          return item;
        }),
      ),
    [],
  );

  const startCart = async () => {
    setBusy(true);
    setError("");
    const res = await apiFetch("/frontdesk_v2/direct_booking/start", {
      method: "POST",
      body: JSON.stringify({
        check_in_date: checkIn,
        check_out_date: checkOut,
      }),
    }).catch(() => null);
    setBusy(false);
    if (!res?.data) {
      setError(res?.message || t("mdb_error_generic"));
      return;
    }
    setItems([]);
    setAddType("");
    setCartExpiresAt(null);
    setCart({ cartId: res.data.cart_id, roomTypes: res.data.room_types || [] });
  };

  const addItem = async () => {
    if (!cart || !addType) return;
    setBusy(true);
    setError("");
    const res = await apiFetch("/frontdesk_v2/direct_booking/items", {
      method: "POST",
      body: JSON.stringify({
        cart_id: cart.cartId,
        room_type_id: addType,
        number_of_rooms: Math.max(1, Number(addQty) || 1),
        check_in_date: checkIn,
        check_out_date: checkOut,
      }),
    }).catch(() => null);
    setBusy(false);
    if (!res?.data) {
      setError(res?.message || t("mdb_error_generic"));
      return;
    }
    const data = res.data;
    setItems((prev) => [
      ...prev.filter((item) => item.sessionId !== data.session_id),
      {
        sessionId: data.session_id,
        roomTypeId: data.room_type_id,
        label: data.room_type_label,
        quantity: data.number_of_rooms,
        status: "draft",
        rooms: data.rooms || [],
        selectedRoomIds: [],
        rateInfo: data.rate_info,
        error: "",
      },
    ]);
    setCartExpiresAt(data.cart_expires_at || null);
    setAddType("");
    setAddQty(1);
  };

  const doConfirmItem = useCallback(
    async (item) => {
      setBusy(true);
      patchItem(item.sessionId, { error: "" });
      const res = await apiFetch(
        `/frontdesk_v2/direct_booking/items/${item.sessionId}/confirm`,
        {
          method: "POST",
          body: JSON.stringify({ room_ids: item.selectedRoomIds }),
        },
      ).catch(() => null);
      setBusy(false);
      if (!res?.data) {
        patchItem(item.sessionId, {
          error: res?.message || t("mdb_error_generic"),
        });
        return;
      }
      setCartExpiresAt(res.data.cart_expires_at || null);
      patchItem(item.sessionId, { status: "confirmed", error: "" });
    },
    [patchItem, t],
  );

  const askConfirmItem = useCallback(
    (item) => {
      setDialog({
        title: t("mdb_confirm_rooms_title"),
        body: t("mdb_confirm_rooms_body")
          .replace("%{count}", item.quantity)
          .replace("%{type}", item.label),
        note: t("mdb_confirm_rooms_note"),
        confirmLabel: t("mdb_confirm_rooms_ok"),
        onConfirm: () => {
          setDialog(null);
          doConfirmItem(item);
        },
        onCancel: () => setDialog(null),
      });
    },
    [doConfirmItem, t],
  );

  const removeItem = useCallback(
    async (item) => {
      setBusy(true);
      const res = await apiFetch(
        `/frontdesk_v2/direct_booking/items/${item.sessionId}`,
        { method: "DELETE" },
      ).catch(() => null);
      setBusy(false);
      if (!res?.data && !res?.message) {
        setError(t("mdb_error_generic"));
        return;
      }
      setCartExpiresAt(res.data?.cart_expires_at || null);
      setItems((prev) =>
        prev.filter((entry) => entry.sessionId !== item.sessionId),
      );
    },
    [t],
  );

  const confirmedItems = items.filter((item) => item.status === "confirmed");
  const allConfirmed =
    items.length > 0 && confirmedItems.length === items.length;
  const totalRupiah = confirmedItems.reduce(
    (sum, item) => sum + Number(item.rateInfo?.total_all_rooms_rupiah || 0),
    0,
  );
  const totalRooms = confirmedItems.reduce(
    (sum, item) => sum + item.quantity,
    0,
  );
  const expiresMs = cartExpiresAt ? Date.parse(cartExpiresAt) : null;
  const secondsLeft =
    expiresMs != null
      ? Math.max(0, Math.floor((expiresMs - now) / 1000))
      : null;
  const timedOut = secondsLeft === 0;
  const holdLevel = timedOut ? "out" : secondsLeft <= 120 ? "warn" : "ok";
  const fmtCountdown = (total) =>
    `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
  const canSubmit =
    allConfirmed && guest.name.trim().length > 0 && !busy && !timedOut;

  const doSubmit = async () => {
    setBusy(true);
    setError("");
    const res = await apiFetch("/frontdesk_v2/direct_booking/submit", {
      method: "POST",
      body: JSON.stringify({
        cart_id: cart.cartId,
        guest_name: guest.name,
        guest_phone: guest.phone,
        guest_email: guest.email,
      }),
    }).catch(() => null);
    if (!res?.data) {
      setBusy(false);
      setError(res?.message || t("mdb_error_generic"));
      return;
    }
    const target = res.data.line_id
      ? `/frontdesk_v2/rooms_grid?date=${checkIn}&drawer=${res.data.line_id}`
      : `/frontdesk_v2/rooms_grid?date=${checkIn}`;
    router.visit(target);
  };

  // Selected rooms (across all confirmed types) that are intentionally blocked — drives the extra
  // explain-everything confirmation before creating the booking.
  const blockedSelections = confirmedItems.flatMap((item) =>
    item.rooms
      .filter((room) => item.selectedRoomIds.includes(room.id) && room.blocked)
      .map((room) => ({
        code: room.code,
        nights: room.blocked_all
          ? t("mdb_blocked_all")
          : (room.blocked_dates || []).join(", "),
      })),
  );

  const askSubmit = () => {
    if (blockedSelections.length > 0) {
      setDialog({
        title: t("mdb_submit_blocked_title"),
        body: (
          <>
            <div>{t("mdb_submit_blocked_intro")}</div>
            <ul style={{ margin: "0.5rem 0 0", paddingLeft: "1.15rem" }}>
              {blockedSelections.map((sel) => (
                <li key={sel.code}>
                  {t("mdb_submit_blocked_room")
                    .replace("%{code}", sel.code)
                    .replace("%{nights}", sel.nights)}
                </li>
              ))}
            </ul>
          </>
        ),
        note: t("mdb_submit_blocked_note"),
        confirmLabel: t("mdb_submit_ok"),
        onConfirm: () => {
          setDialog(null);
          doSubmit();
        },
        onCancel: () => setDialog(null),
      });
      return;
    }

    setDialog({
      title: t("mdb_submit_title"),
      body: t("mdb_submit_body")
        .replace("%{rooms}", totalRooms)
        .replace("%{types}", confirmedItems.length)
        .replace("%{guest}", guest.name.trim())
        .replace("%{total}", rupiah(totalRupiah)),
      confirmLabel: t("mdb_submit_ok"),
      onConfirm: () => {
        setDialog(null);
        doSubmit();
      },
      onCancel: () => setDialog(null),
    });
  };

  const cancelAll = async () => {
    if (cart) {
      await apiFetch("/frontdesk_v2/direct_booking/cancel", {
        method: "POST",
        body: JSON.stringify({ cart_id: cart.cartId }),
      }).catch(() => null);
    }
    setCart(null);
    setItems([]);
    setGuest({ name: "", phone: "", email: "" });
    setError("");
    setCartExpiresAt(null);
    loadRecovery();
  };

  const availableTypes = (cart?.roomTypes || []).filter(
    (type) => !items.some((item) => item.roomTypeId === type.room_type_id),
  );

  const startMs = Date.parse(checkIn);
  const endMs = Date.parse(checkOut);
  const nights =
    Number.isNaN(startMs) || Number.isNaN(endMs)
      ? 0
      : Math.max(0, Math.round((endMs - startMs) / 86_400_000));

  return (
    <PageShell>
      <Head title={t("mdb_page_title")} />
      <Wrap>
        {error ? <PageError>{error}</PageError> : null}

        {cart && items.length > 0 && secondsLeft !== null ? (
          <HoldBar $level={holdLevel}>
            <Timer size={15} />
            {timedOut
              ? t("mdb_expired")
              : `${t("mdb_hold_timer")}: ${fmtCountdown(secondsLeft)}`}
          </HoldBar>
        ) : null}

        <Panel>
          <PanelTitle>{t("mdb_dates_title")}</PanelTitle>
          <Row>
            <DatePickField
              label={t("check_in")}
              value={checkIn}
              onChange={setCheckIn}
            />
            <DatePickField
              label={t("check_out")}
              value={checkOut}
              min={shiftIso(checkIn, 1)}
              onChange={setCheckOut}
            />
            <NightsBadge $invalid={nights <= 0}>
              <Moon size={15} />
              {nights} {t("nights")}
            </NightsBadge>
            <Button type="button" onClick={startCart} disabled={busy}>
              {busy ? <Loader2 size={15} /> : <BedDouble size={15} />}
              {cart ? t("mdb_reload_types") : t("mdb_load_types")}
            </Button>
            {cart ? (
              <GhostButton type="button" onClick={cancelAll} disabled={busy}>
                {t("mdb_reset")}
              </GhostButton>
            ) : null}
          </Row>
        </Panel>

        {cart ? (
          <Panel>
            <PanelTitle>{t("mdb_rooms_title")}</PanelTitle>
            <Row>
              <Field>
                {t("mdb_room_type")}
                <Select
                  value={addType}
                  onChange={(event) => setAddType(event.target.value)}
                >
                  <option value="">{t("mdb_pick_type")}</option>
                  {availableTypes.map((type) => (
                    <option key={type.room_type_id} value={type.room_type_id}>
                      {type.room_type_label} ({type.min_available})
                    </option>
                  ))}
                </Select>
              </Field>
              <Field>
                {t("mdb_qty")}
                <NumberInput
                  type="number"
                  min="1"
                  value={addQty}
                  // Allow the field to go empty while typing (staff clear it before retyping "2");
                  // only snap back to a valid count on blur.
                  onChange={(event) => {
                    const raw = event.target.value;
                    if (raw === "" || /^\d+$/.test(raw)) {
                      setAddQty(raw);
                    }
                  }}
                  onBlur={() =>
                    setAddQty((prev) => Math.max(1, Number(prev) || 1))
                  }
                />
              </Field>
              <Button
                type="button"
                onClick={addItem}
                disabled={busy || !addType}
              >
                <Plus size={15} /> {t("mdb_add")}
              </Button>
            </Row>

            <div style={{ marginTop: "1rem" }}>
              {items.length === 0 ? (
                <Muted>{t("mdb_no_items")}</Muted>
              ) : (
                items.map((item) => (
                  <CartItemCard
                    key={item.sessionId}
                    item={item}
                    busy={busy}
                    onToggleRoom={onToggleRoom}
                    onConfirm={askConfirmItem}
                    onRemove={removeItem}
                  />
                ))
              )}
            </div>
          </Panel>
        ) : null}

        {confirmedItems.length > 0 ? (
          <Panel>
            <PanelTitle>{t("mdb_guest_title")}</PanelTitle>
            <GuestForm guest={guest} onChange={onGuestChange} />
          </Panel>
        ) : null}

        {items.length > 0 ? (
          <SummaryBar>
            <Total>
              {totalRooms} {t("mdb_rooms_unit")} · {rupiah(totalRupiah)}
            </Total>
            <Button type="button" onClick={askSubmit} disabled={!canSubmit}>
              <Check size={16} /> {t("mdb_submit")}
            </Button>
          </SummaryBar>
        ) : null}
      </Wrap>

      <RecoveryDock
        currentCartId={cart?.cartId || null}
        cartExpiresAt={cartExpiresAt}
        activeCarts={activeCarts}
        recentBookings={recentBookings}
        now={now}
        onResume={resumeCart}
        onOpenBooking={openBooking}
      />

      {dialog ? (
        <ConfirmDialog
          title={dialog.title}
          body={dialog.body}
          note={dialog.note}
          confirmLabel={dialog.confirmLabel}
          onConfirm={dialog.onConfirm}
          onCancel={dialog.onCancel}
        />
      ) : null}
    </PageShell>
  );
};

export default DirectBookingPage;
